import { DailyStock } from "@/api/daily-stock-api";
import { z } from "zod";
import { BaseModelFields } from "./base-model";

export interface Product extends BaseModelFields {
  code: string;
  saleCode?: string | null;
  name: string;
  description?: string | null;
  unit?: string | null;
  quantity?: number | null;
  price: number;
  cost?: number | null;
  imageUrl?: string | null;
  sold?: number | null;
  pinned?: boolean | null;
  onStoreFront?: boolean | null;
  categoryId?: string | null;
  categoryName?: string | null;
}

export interface ProductVariant extends BaseModelFields {
  productId: string;
  storeId: number;
  sku: string;
  name: string;
  variantKey: string; // JSON string from backend
  price?: number | null; // Double value in dollars
  cost?: number | null; // Double value in dollars
  priceAdjustment?: string | null; // Keep for backward compatibility
  costAdjustment?: string | null; // Keep for backward compatibility
  quantity?: number | null;
  imageUrl?: string | null;
  stock?: DailyStock; // Stock information for this variant
  sold?: number | null;
  productName?: string | null;
  pinned?: boolean;
  color?: string | null;
  size?: string | null;

}

export interface ProductAttribute extends BaseModelFields {
  productId: string;
  name: string;
  displayName: string;
  type: string;
  attributeValues?: AttributeValue[]; // Nested attribute values from API
}

export interface AttributeValue extends BaseModelFields {
  attributeId: string;
  value: string;
  displayValue: string;
  colorCode?: string | null;
  sortOrder?: number | null;
}

export interface ProductMedia extends BaseModelFields {
  productId: string;
  mediaAssetId: string;
  sortOrder?: number | null;
}

// Product form schema for create/update operations
// Matches ProductCreateRequest DTO structure
export const productSchema = z.object({
  code: z.string().min(1, "Product code is required"),
  name: z.string().min(1, "Product name is required"),
  isActive: z.boolean().optional(),
  onStoreFront: z.boolean().optional(),
  categoryId: z.string().nullable().optional(),
  description: z.string().optional(),
  unit: z.string().optional(),
  initStock: z.number(),
  price: z
    .number()
    .min(0, "Price must be 0 or greater")
    .refine(
      (val) => {
        // If value is 0, it's valid
        if (val === 0) return true;
        // If value is > 0, check decimal places
        const rounded = Math.round(val * 100) / 100;
        return Math.abs(val - rounded) < 0.001;
      },
      { message: "Price must have at most 2 decimal places" }
    ),
  cost: z
    .number()
    .min(0, "Cost must be 0 or greater")
    .refine(
      (val) => {
        // If value is 0, it's valid
        if (val === 0) return true;
        // If value is > 0, check decimal places
        const rounded = Math.round(val * 100) / 100;
        return Math.abs(val - rounded) < 0.001;
      },
      { message: "Cost must have at most 2 decimal places" }
    ),
  imageUrl: z.string().optional(),
  attributes: z
    .array(
      z.object({
        name: z.string(),
        values: z.array(z.string()),
      })
    )
    .optional(),
  // Temporary fields for form UI - will be transformed to attributes on submit
  colorValues: z.array(z.string()).optional(),
  sizeValues: z.array(z.string()).optional(),
});

// Product form values type
export type ProductFormValues = z.infer<typeof productSchema>;

// Product update values type - matches ProductUpdateRequest DTO
// All fields optional, NO attributes field
export type ProductUpdateValues = {
  code?: string;
  name?: string;
  description?: string;
  unit?: string;
  price?: number;
  cost?: number;
  imageUrl?: string;
  isActive?: boolean;
  onStoreFront?: boolean;
  categoryId?: string | number | null;
};

// Product search parameters
export type ProductSearchParams = {
  page: string;
  size: string;
  sort: string;
  search?: string;
};

export type ProductVariantAttribute = {
  id: string;
  variantId: string;
  attributeId: string;
  valueId: string;
  createdAt: Date;
  updatedAt: Date;
};

// Product inventory types
export type ProductInventory = {
  id: string;
  productId: string;
  storeId: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  lastUpdated: Date;
};

// Product stock movement types
export type StockMovement = {
  id: string;
  productId: string;
  storeId: string;
  type: "in" | "out" | "adjustment" | "transfer";
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  reason: string;
  reference?: string;
  notes?: string;
  createdBy: string;
  createdAt: Date;
};

// Product filters
export type ProductFilters = {
  search?: string;
  category?: string;
  isActive?: boolean;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  storeId?: string;
};

// Product sort options
export type ProductSortOption =
  | "name,asc"
  | "name,desc"
  | "price,asc"
  | "price,desc"
  | "createdAt,asc"
  | "createdAt,desc"
  | "quantity,asc"
  | "quantity,desc";

// Product bulk operations
export type ProductBulkUpdate = {
  productIds: string[];
  updates: Partial<ProductFormValues>;
};

export type ProductBulkDelete = {
  productIds: string[];
  reason?: string;
};

export type TProductWithRelation = {
  product: Product;
  productAttributes: ProductAttribute[];
  productVariants: ProductVariant[];
};

// ProductResponse from backend search endpoint
export interface ProductResponse extends BaseModelFields {
  code: string;
  name: string;
  description?: string | null;
  unit?: string | null;
  price: number;
  cost?: number | null;
  imageUrl?: string | null;
  lowStockQuantity?: number | null;
  onStoreFront?: boolean | null;
  categoryId?: string | null;
  categoryName?: string | null;
  variants?: ProductVariant[] | [];
}

// Sort configuration for pagination
export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

// Pagination request for search endpoint (legacy format)
export interface PaginationRequest {
  search?: string;
  filters?: Record<string, any>;
  sorts?: SortConfig[];
  page: number;
  size: number;
}

// Search property for FilterRequest
export interface SearchProp {
  propName: string;
  value: string;
}

// Filter request matching backend Java FilterRequest format
export interface FilterRequest {
  includeDeleted?: boolean;
  filterText?: string | null;
  searchProp?: SearchProp[] | null;
  page: number;
  size: number;
  sortBy: string;
  ordinal: 'asc' | 'desc';
}

// Variant with relation response from backend
export interface VariantWithRelationResponse {
  variant: ProductVariant;
  stock: DailyStock;
}

// Variant stock search item from /products/search endpoint
export interface VariantStockSearchItem {
  productCode?: string;
  productDeletedAt?: string | null;
  variantDeletedAt?: string | null;
  id: number;
  variantName: string;
  variantKey: string | null;
  sku: string;
  imageUrl: string;
  price: number;
  pinned: boolean;
  productId: string;
  productName: string;
  productImageUrl: string;
  saleCode: string | null;
  description: string | null;
  unit: string | null;
  productPrice: number;
  soldQty: number;
  stockInHand: number;
  openQty: number;
  closingQty: number;
  adjustInQty: number;
  adjustOutQty: number;
  receiveQty: number;
  dailyStockId?: string | number;
  isActive?: boolean;
  onStoreFront?: boolean;
  categoryId?: string | null;
  categoryName?: string | null;
}

// Product with variants stock grouped by productId
export interface ProductWithVariantsStock {
  productId: string;
  productName: string;
  productImageUrl: string;
  productPrice: number;
  isActive?: boolean;
  onStoreFront?: boolean;
  categoryId?: string | null;
  categoryName?: string | null;
  variants: VariantStockSearchItem[];
}
